import { z } from "zod";
import {
  BiddingStrategySchema,
  CalendarDateSchema,
  NegativeMatchTypeSchema,
  PlacementSchema,
} from "../campaign-creation";
import {
  SMART_CREATION_MAX_CAMPAIGNS,
  SMART_CREATION_MAX_DRAFT_ITEMS,
  SmartCreationDraftProductSchema,
  SmartCreationTargetingStrategyKindSchema,
} from "../smart-creation";
import {
  auditedValueSchema,
  BidSchema,
  BudgetSchema,
  EntityIdSchema,
  LineageKeySchema,
  NameSchema,
  PlacementPercentageSchema,
  SmartCreationReviewAdGroupRefSchema,
  SmartCreationReviewBudgetAllocationSchema,
  SmartCreationReviewCampaignRefSchema,
  SmartCreationReviewNamingRuleSchema,
  SmartCreationReviewNegativeKeywordRefSchema,
  SmartCreationReviewNegativeProductRefSchema,
  SmartCreationReviewPlanRefSchema,
  SmartCreationReviewTargetRefSchema,
} from "./values";

const AuditedNameSchema = auditedValueSchema(NameSchema);
const AuditedNullablePortfolioSchema = auditedValueSchema(
  z.string().trim().min(1).max(128).nullable(),
);
const AuditedBudgetSchema = auditedValueSchema(BudgetSchema);
const AuditedBidSchema = auditedValueSchema(BidSchema);
const AuditedBooleanSchema = auditedValueSchema(z.boolean());
const AuditedBiddingStrategySchema = auditedValueSchema(BiddingStrategySchema);
const AuditedBudgetAllocationSchema = auditedValueSchema(SmartCreationReviewBudgetAllocationSchema);
const AuditedKeywordMatchTypeSchema = auditedValueSchema(z.enum(["broad", "phrase", "exact"]));
const AuditedNegativeMatchTypeSchema = auditedValueSchema(NegativeMatchTypeSchema);
const AuditedKeywordSchema = auditedValueSchema(z.string().trim().min(1).max(80));
const AuditedAsinSchema = auditedValueSchema(
  z
    .string()
    .trim()
    .regex(/^[A-Z0-9]{10}$/),
);

export const SmartCreationReviewPlanSettingsSchema = z
  .object({
    ref: SmartCreationReviewPlanRefSchema,
    name: AuditedNameSchema,
    portfolioId: AuditedNullablePortfolioSchema,
    namingRule: SmartCreationReviewNamingRuleSchema.optional(),
    startDate: CalendarDateSchema,
    endDate: CalendarDateSchema.nullable(),
  })
  .strict()
  .superRefine((settings, ctx) => {
    if (settings.endDate && settings.endDate < settings.startDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "endDate must be on or after startDate",
        path: ["endDate"],
      });
    }
  });
export type SmartCreationReviewPlanSettings = z.infer<typeof SmartCreationReviewPlanSettingsSchema>;

export const SmartCreationReviewPlacementSchema = z
  .object({
    placement: PlacementSchema,
    percentage: auditedValueSchema(PlacementPercentageSchema),
  })
  .strict();

export const SmartCreationReviewCampaignSchema = z
  .object({
    ref: SmartCreationReviewCampaignRefSchema,
    name: AuditedNameSchema,
    budgetAllocation: AuditedBudgetAllocationSchema,
    dailyBudget: AuditedBudgetSchema,
    biddingStrategy: AuditedBiddingStrategySchema,
    placements: z.array(SmartCreationReviewPlacementSchema).max(4),
    excluded: AuditedBooleanSchema,
    adGroupLineageKeys: z.array(LineageKeySchema).min(1).max(3_000),
  })
  .strict()
  .superRefine((campaign, ctx) => {
    const placements = campaign.placements.map((placement) => placement.placement);
    if (new Set(placements).size !== placements.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Campaign placements must be unique",
        path: ["placements"],
      });
    }
    if (new Set(campaign.adGroupLineageKeys).size !== campaign.adGroupLineageKeys.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Campaign Ad Group lineage keys must be unique",
        path: ["adGroupLineageKeys"],
      });
    }
  });
export type SmartCreationReviewCampaign = z.infer<typeof SmartCreationReviewCampaignSchema>;

export const SmartCreationReviewAdGroupSchema = z
  .object({
    ref: SmartCreationReviewAdGroupRefSchema,
    campaignLineageKey: LineageKeySchema,
    name: AuditedNameSchema,
    defaultBid: AuditedBidSchema,
    products: z.array(SmartCreationDraftProductSchema).min(1).max(500),
    targetLineageKeys: z.array(LineageKeySchema).min(1).max(500),
    negativeLineageKeys: z.array(LineageKeySchema).max(1_000),
  })
  .strict();
export type SmartCreationReviewAdGroup = z.infer<typeof SmartCreationReviewAdGroupSchema>;

const SmartCreationReviewTargetBaseShape = {
  ref: SmartCreationReviewTargetRefSchema,
  adGroupLineageKey: LineageKeySchema,
  sourceTargetId: EntityIdSchema,
  strategyKind: SmartCreationTargetingStrategyKindSchema,
  bid: AuditedBidSchema,
  excluded: AuditedBooleanSchema,
  origin: z.enum(["generated", "user_added"]),
};

export const SmartCreationReviewTargetSchema = z.discriminatedUnion("type", [
  z
    .object({
      ...SmartCreationReviewTargetBaseShape,
      type: z.literal("automatic"),
      expressionType: z.enum(["close_match", "loose_match", "substitutes", "complements"]),
    })
    .strict(),
  z
    .object({
      ...SmartCreationReviewTargetBaseShape,
      type: z.literal("keyword"),
      keyword: z.string().trim().min(1).max(80),
      matchType: AuditedKeywordMatchTypeSchema,
    })
    .strict(),
  z
    .object({
      ...SmartCreationReviewTargetBaseShape,
      type: z.literal("product"),
      matchType: z.enum(["exact", "expanded"]).optional(),
      asin: z
        .string()
        .trim()
        .regex(/^[A-Z0-9]{10}$/),
    })
    .strict(),
  z
    .object({
      ...SmartCreationReviewTargetBaseShape,
      type: z.literal("category"),
      categoryId: z.string().trim().min(1).max(128),
    })
    .strict(),
  z
    .object({
      ...SmartCreationReviewTargetBaseShape,
      type: z.literal("category_brand"),
      categoryId: z.string().regex(/^\d+$/),
      brandId: z.string().trim().min(1).max(128),
    })
    .strict(),
]);
export type SmartCreationReviewTarget = z.infer<typeof SmartCreationReviewTargetSchema>;

export const SmartCreationReviewNegativeSchema = z.discriminatedUnion("type", [
  z
    .object({
      type: z.literal("keyword"),
      ref: SmartCreationReviewNegativeKeywordRefSchema,
      adGroupLineageKey: LineageKeySchema,
      text: AuditedKeywordSchema,
      matchType: AuditedNegativeMatchTypeSchema,
      origin: z.enum(["preview_generator", "user"]),
    })
    .strict(),
  z
    .object({
      type: z.literal("product"),
      ref: SmartCreationReviewNegativeProductRefSchema,
      adGroupLineageKey: LineageKeySchema,
      asin: AuditedAsinSchema,
      origin: z.enum(["preview_generator", "user"]),
    })
    .strict(),
]);
export type SmartCreationReviewNegative = z.infer<typeof SmartCreationReviewNegativeSchema>;

export const SmartCreationReviewGraphSchema = z
  .object({
    campaigns: z.array(SmartCreationReviewCampaignSchema).min(1).max(SMART_CREATION_MAX_CAMPAIGNS),
    adGroups: z.array(SmartCreationReviewAdGroupSchema).min(1).max(SMART_CREATION_MAX_DRAFT_ITEMS),
    targets: z.array(SmartCreationReviewTargetSchema).min(1).max(SMART_CREATION_MAX_DRAFT_ITEMS),
    negatives: z.array(SmartCreationReviewNegativeSchema).max(SMART_CREATION_MAX_DRAFT_ITEMS),
  })
  .strict();
export type SmartCreationReviewGraph = z.infer<typeof SmartCreationReviewGraphSchema>;

const CampaignProjectionSchema = z
  .object({
    campaignLineageKeys: z
      .array(LineageKeySchema)
      .min(1)
      .max(SMART_CREATION_MAX_CAMPAIGNS)
      .refine((keys) => new Set(keys).size === keys.length, {
        message: "Projection Campaign lineage keys must be unique",
      }),
  })
  .strict();

export const SmartCreationReviewProjectionsSchema = z
  .object({
    structure: CampaignProjectionSchema,
    table: CampaignProjectionSchema,
  })
  .strict()
  .superRefine((projections, ctx) => {
    const structure = [...projections.structure.campaignLineageKeys].sort();
    const table = [...projections.table.campaignLineageKeys].sort();
    if (structure.length !== table.length || structure.some((key, index) => key !== table[index])) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Structure and Table projections must contain the same Campaign set",
        path: ["table", "campaignLineageKeys"],
      });
    }
  });
export type SmartCreationReviewProjections = z.infer<typeof SmartCreationReviewProjectionsSchema>;
